import { FormInstance } from 'antd'
import { SagaIterator } from 'redux-saga'
import { call, fork, put, takeLatest } from 'redux-saga/effects'
import {
  ACTIVATE_DOCTOR,
  ADD_DOCTOR,
  CHANGE_DOCTOR_ROLE,
  DELETE_DOCTOR,
  GET_DOCTOR,
  GET_DOCTORS,
  SET_UPDATE_DOCTOR,
  UPDATE_DOCTOR,
} from './actionTypes'
import {
  activateDoctorData,
  addDoctorData,
  changeDoctorRoleData,
  deleteDoctorData,
  getDoctorData,
  getDoctorsData,
  updateDoctorData,
} from './api'
import {
  addDoctor,
  addDoctors,
  addDoctorSuccess,
  deleteDoctorSuccess,
  errorDoctor,
  getDoctors,
  setDoctorForUpdateSuccess,
  successDoctor,
  updateDoctorSuccess,
} from './doctorReducer'
import { Doctor, DoctorListType, GetDoctorDataType } from './types'

type Action = {
  type: string
  payload: string
}

type postAction = {
  type: string
  payload: Doctor
}

export function* handleGetDoctorData(actions: Action): SagaIterator<void> {
  try {
    const response = yield call(getDoctorData, actions.payload)
    const { data } = response
    yield put(addDoctor(data))
    yield put(getDoctors())
  } catch (error: any) {
    yield put(errorDoctor(error.response.data.message))
  }
}

export function* handleGetDoctorsData(actions: Action): SagaIterator<void> {
  try {
    const response = yield call(getDoctorsData)
    const { data } = response
    let unmodifiedData = data.doctors
    let newData: Doctor[] = unmodifiedData.map((data: DoctorListType) => {
      let newData: Doctor = {
        did: data.doctorId,
        doctorKey: data.doctorKey,
        emailId: data.email,
        firstName: data.firstName,
        lastName: data.lastName,
        isSupervisor: data.isSupervisor,
        jobTitle: data.jobTitle,
        phonePrimary: data.phone1,
        phoneSecondary: data.phone2,
        title: data.title,
        isActive: data.isActive,
      }
      return newData
    })
    yield put(addDoctors(newData))
  } catch (error: any) {
    yield put(errorDoctor(error.response.data.message))
  }
}

export function* handleDeleteDoctorData(actions: Action): SagaIterator<void> {
  try {
    const response = yield call(deleteDoctorData, actions.payload)
    yield put(deleteDoctorSuccess(actions.payload))
    yield put(getDoctors())
    const { data } = response
    yield put(successDoctor(data.status))
  } catch (error: any) {
    yield put(errorDoctor(error.response.data.message))
  }
}

export function* handleAddDoctorData(actions: postAction): SagaIterator<void> {
  try {
    const response = yield call(addDoctorData, actions.payload)
    const { data } = response
    const newDoctorData = {
      ...actions.payload,
      doctorKey: data.doctorKey,
    }
    yield put(addDoctorSuccess(newDoctorData))
    yield put(getDoctors())
    yield put(successDoctor(data.status))
  } catch (error: any) {
    yield put(errorDoctor(error.response.data.message))
  }
}

export function* handleUpdateDoctorData(
  actions: postAction
): SagaIterator<void> {
  try {
    const response = yield call(
      updateDoctorData,
      actions.payload.doctorKey,
      actions.payload
    )
    const { data } = response
    yield put(updateDoctorSuccess(actions.payload))
    yield put(getDoctors())
    yield put(successDoctor(data.status))
  } catch (error: any) {
    yield put(errorDoctor(error.response.data.message))
  }
}

export function* handleActivateDoctorData(actions: Action): SagaIterator<void> {
  try {
    const response = yield call(activateDoctorData, actions.payload)
    yield put(getDoctors())
    const { data } = response
    yield put(successDoctor(data.status))
  } catch (error: any) {
    yield put(errorDoctor(error.response.data.message))
  }
}

type ChangeDoctorRoleDataAction = {
  type: string
  payload: {
    id: string
    role: string
  }
}

export function* handleChangeDoctorRoleData(
  actions: ChangeDoctorRoleDataAction
): SagaIterator<void> {
  try {
    const response = yield call(
      changeDoctorRoleData,
      actions.payload.id,
      actions.payload.role
    )
    yield put(getDoctors())
    const { data } = response
    yield put(successDoctor(data.status))
  } catch (error: any) {
    yield put(errorDoctor(error.response.data.message))
  }
}

type updateDoctorAction = {
  type: string
  payload: {
    key: string
    form: FormInstance<any>
    dob: string
  }
}

export function* handleSetUpdateDoctorData(
  actions: updateDoctorAction
): SagaIterator<void> {
  const response = yield call(getDoctorData, actions.payload.key)
  const { data } = response
  for (const key in data) {
    if (data[key] === null || data[key] === undefined) {
      data[key] = ''
    }
  }
  const unmodifiedData: GetDoctorDataType = data
  let newDoctorData: Doctor = {
    did: unmodifiedData.doctorId,
    doctorKey: unmodifiedData.doctorKey,
    emailId: unmodifiedData.email,
    isActive: unmodifiedData.isActive,
    title: unmodifiedData.title,
    firstName: unmodifiedData.firstName,
    lastName: unmodifiedData.lastName,
    isSupervisor: unmodifiedData.isSupervisor,
    jobTitle: unmodifiedData.jobTitle,
    phonePrimary: unmodifiedData.phone1,
    phoneSecondary: unmodifiedData.phone2,
  }

  yield put(
    setDoctorForUpdateSuccess({
      form: actions.payload.form,
      doctorData: newDoctorData,
    })
  )
}

export function* watcherSaga() {
  yield takeLatest(GET_DOCTOR, handleGetDoctorData)
  yield takeLatest(GET_DOCTORS, handleGetDoctorsData)
  yield takeLatest(ADD_DOCTOR, handleAddDoctorData)
  yield takeLatest(DELETE_DOCTOR, handleDeleteDoctorData)
  yield takeLatest(UPDATE_DOCTOR, handleUpdateDoctorData)
  yield takeLatest(ACTIVATE_DOCTOR, handleActivateDoctorData)
  yield takeLatest(SET_UPDATE_DOCTOR, handleSetUpdateDoctorData)
  yield takeLatest(CHANGE_DOCTOR_ROLE, handleChangeDoctorRoleData)
}

export const doctorSaga = [fork(watcherSaga)]
